// pages/admin/AdminTeam.jsx — Équipe & Accès (Users + Sites + Droits)
import { useAuth } from '../../utils/auth';
// refreshPerms available via useAuth
import { useState, useEffect, useCallback } from 'react';
import { auth as authApi, rooms as roomsApi, gmSkills as gmSkillsApi,
         sites as sitesApi, permissionsApi } from '../../utils/api';
import { Plus, Edit2, Award, MapPin, Users, DoorOpen,
         Trash2, Shield, Check, X, Minus } from 'lucide-react';
import toast from 'react-hot-toast';
import ConfirmModal from '../../components/ConfirmModal';

const ROLES = ['admin','manager','staff','gm','freelance'];
const ROLE_COLORS = { admin:'badge-red', manager:'badge-amber', staff:'badge-purple', gm:'badge-teal', freelance:'badge-gray' };
const SITE_COLORS = ['#7c5cfc','#00d2b4','#f5a623','#ff4d6d','#22d3a0','#3b82f6','#a387fd','#f97316'];
const ROLE_LABELS = { admin:'Admin', manager:'Manager', staff:'Staff', gm:'GM', freelance:'Freelance' };

// ── Users ─────────────────────────────────────────────────────
function SkillsModal({ user, rooms, onClose, onSaved }) {
  const [selected, setSelected] = useState([]);
  const [loading, setLoading]   = useState(false);
  useEffect(() => { gmSkillsApi.byUser(user.id).then(r=>setSelected(r.data.skills.map(s=>s.room_id))); },[user.id]);
  const save = async () => {
    setLoading(true);
    try { await gmSkillsApi.setUserSkills(user.id,{roomIds:selected}); toast.success('Sauvegardé'); onSaved(); onClose(); }
    catch { toast.error('Erreur'); } finally { setLoading(false); }
  };
  return (
    <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div className="modal">
        <h3 className="modal-title">Compétences — {user.first_name}</h3>
        <div style={{display:'flex',flexWrap:'wrap',gap:8,marginBottom:20}}>
          {rooms.map(r=>{const sel=selected.includes(r.id); return (
            <div key={r.id} onClick={()=>setSelected(p=>sel?p.filter(i=>i!==r.id):[...p,r.id])}
              style={{padding:'6px 14px',borderRadius:8,cursor:'pointer',fontSize:13,fontWeight:600,
                background:sel?'rgba(124,92,252,0.15)':'var(--bg-elevated)',
                border:`1px solid ${sel?'var(--accent)':'var(--border)'}`,
                color:sel?'var(--accent-light)':'var(--text-2)'}}>
              {r.name}
            </div>
          );})}
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Annuler</button>
          <button className="btn btn-primary" onClick={save} disabled={loading}>Enregistrer</button>
        </div>
      </div>
    </div>
  );
}

function UserModal({ user, onClose, onSaved }) {
  const [form, setForm] = useState({
    firstName:user?.first_name||'', lastName:user?.last_name||'',
    email:user?.email||'', phone:user?.phone||'',
    role:user?.role||'staff', password:'', isActive:user?.is_active!==false,
  });
  const [loading, setLoading] = useState(false);
  const f = k => ({value:form[k], onChange:e=>setForm(p=>({...p,[k]:e.target.value}))});
  const submit = async e => {
    e.preventDefault(); setLoading(true);
    try {
      const d = {firstName:form.firstName,lastName:form.lastName,email:form.email,
        phone:form.phone,role:form.role,isActive:form.isActive};
      if (form.password) d.password = form.password;
      if (user) await authApi.updateUser(user.id,d);
      else await authApi.createUser({...d,password:form.password});
      toast.success(user?'Mis à jour':'Créé'); onSaved(); onClose();
    } catch(e) { toast.error(e.response?.data?.error||'Erreur'); }
    finally { setLoading(false); }
  };
  return (
    <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div className="modal">
        <h3 className="modal-title">{user?'Modifier':'Créer'} un utilisateur</h3>
        <form onSubmit={submit}>
          <div className="form-row">
            <div className="form-group"><label className="form-label">Prénom *</label><input className="input" required {...f('firstName')}/></div>
            <div className="form-group"><label className="form-label">Nom *</label><input className="input" required {...f('lastName')}/></div>
          </div>
          <div className="form-group"><label className="form-label">Email *</label><input type="email" className="input" required {...f('email')}/></div>
          <div className="form-row">
            <div className="form-group"><label className="form-label">Téléphone</label><input className="input" {...f('phone')}/></div>
            <div className="form-group">
              <label className="form-label">Rôle</label>
              <select className="input" {...f('role')}>{ROLES.map(r=><option key={r} value={r}>{r}</option>)}</select>
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">{user?'Nouveau mot de passe (vide = inchangé)':'Mot de passe *'}</label>
            <input type="password" className="input" required={!user} {...f('password')}/>
          </div>
          <div className="form-group">
            <label style={{display:'flex',alignItems:'center',gap:8,cursor:'pointer'}}>
              <input type="checkbox" checked={form.isActive} onChange={e=>setForm(p=>({...p,isActive:e.target.checked}))}/>
              Compte actif
            </label>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Annuler</button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading?<span className="spinner" style={{width:14,height:14}}/>:(user?'Mettre à jour':'Créer')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function UsersTab({ can }) {
  const [users, setUsers] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [editUser, setEdit] = useState(null);
  const [skillsUser, setSkills] = useState(null);
  const [showNew, setNew] = useState(false);
  const load = useCallback(() => {
    authApi.users().then(r=>setUsers(r.data.users)).catch(()=>{});
    roomsApi.list().then(r=>setRooms(r.data.rooms)).catch(()=>{});
  },[]);
  useEffect(()=>{ load(); },[load]);
  return (
    <div>
      <div style={{display:'flex',justifyContent:'flex-end',marginBottom:16}}>
        <button className="btn btn-primary btn-sm" onClick={()=>setNew(true)}><Plus size={14}/> Nouvel utilisateur</button>
      </div>
      <div className="card" style={{padding:0,overflow:'hidden'}}>
        <div className="table-wrap">
          <table>
            <thead><tr><th>Nom</th><th>Email</th><th>Rôle</th><th>Statut</th><th>Actions</th></tr></thead>
            <tbody>
              {users.map(u=>(
                <tr key={u.id}>
                  <td><div style={{fontWeight:600}}>{u.first_name} {u.last_name}</div></td>
                  <td style={{color:'var(--text-3)',fontSize:13}}>{u.email}</td>
                  <td><span className={`badge ${ROLE_COLORS[u.role]||'badge-gray'}`}>{u.role}</span></td>
                  <td><span className={`badge ${u.is_active?'badge-green':'badge-gray'}`}>{u.is_active?'Actif':'Inactif'}</span></td>
                  <td>
                    <div style={{display:'flex',gap:6}}>
                      <button className="btn btn-secondary btn-sm" onClick={()=>setEdit(u)}><Edit2 size={13}/></button>
                      {['gm','freelance','staff'].includes(u.role) &&
                        <button className="btn btn-secondary btn-sm" onClick={()=>setSkills(u)} title="Compétences salles"><Award size={13}/></button>
                      }
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      {(showNew||editUser) && <UserModal user={editUser} onClose={()=>{setEdit(null);setNew(false);}} onSaved={load}/>}
      {skillsUser && <SkillsModal user={skillsUser} rooms={rooms} onClose={()=>setSkills(null)} onSaved={load}/>}
    </div>
  );
}

// ── Sites ─────────────────────────────────────────────────────
function SiteModal({ site, onClose, onSaved }) {
  const [form, setForm] = useState({name:site?.name||'',address:site?.address||'',color:site?.color||'#7c5cfc',displayOrder:site?.display_order||0});
  const [loading, setLoading] = useState(false);
  const f = k => ({value:form[k], onChange:e=>setForm(p=>({...p,[k]:e.target.value}))});
  const submit = async e => {
    e.preventDefault(); setLoading(true);
    try {
      if (site) await sitesApi.update(site.id,{name:form.name,address:form.address,color:form.color,displayOrder:form.displayOrder});
      else await sitesApi.create({name:form.name,address:form.address,color:form.color,displayOrder:form.displayOrder});
      toast.success(site?'Site mis à jour':'Site créé'); onSaved(); onClose();
    } catch(e) { toast.error(e.response?.data?.error||'Erreur'); }
    finally { setLoading(false); }
  };
  return (
    <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div className="modal">
        <h3 className="modal-title">{site?'Modifier':'Créer'} un site</h3>
        <form onSubmit={submit}>
          <div className="form-group"><label className="form-label">Nom *</label><input className="input" required {...f('name')}/></div>
          <div className="form-group"><label className="form-label">Adresse</label><input className="input" {...f('address')}/></div>
          <div className="form-group">
            <label className="form-label">Couleur</label>
            <div style={{display:'flex',gap:8,flexWrap:'wrap',marginTop:4}}>
              {SITE_COLORS.map(col=>(
                <div key={col} onClick={()=>setForm(p=>({...p,color:col}))}
                  style={{width:28,height:28,borderRadius:'50%',background:col,cursor:'pointer',
                    border:form.color===col?'3px solid white':'3px solid transparent',
                    boxShadow:form.color===col?`0 0 0 2px ${col}`:'none'}}/>
              ))}
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Annuler</button>
            <button type="submit" className="btn btn-primary" disabled={loading}>{site?'Mettre à jour':'Créer'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function SitesTab({ can }) {
  const [sites, setSites] = useState([]);
  const [editSite, setEdit] = useState(null);
  const [showNew, setNew] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(null);
  const [removing, setRemoving] = useState(false);
  const load = useCallback(() => { sitesApi.list().then(r=>setSites(r.data.sites)).catch(()=>{}); },[]);
  useEffect(()=>{ load(); },[load]);
  const remove = async () => {
    setRemoving(true);
    try {
      await sitesApi.remove(confirmRemove.id);
      toast.success('Supprimé');
      setConfirmRemove(null);
      load();
    } catch(e) { toast.error(e.response?.data?.error||'Erreur'); }
    finally { setRemoving(false); }
  };
  return (
    <div>
      <div style={{display:'flex',justifyContent:'flex-end',marginBottom:16}}>
        <button className="btn btn-primary btn-sm" onClick={()=>setNew(true)}><Plus size={14}/> Nouveau site</button>
      </div>
      <div style={{display:'flex',flexDirection:'column',gap:12}}>
        {sites.map(s=>(
          <div key={s.id} className="card">
            <div style={{display:'flex',alignItems:'center',gap:14}}>
              <div style={{width:16,height:48,borderRadius:4,background:s.color,flexShrink:0}}/>
              <div style={{flex:1}}>
                <div style={{fontWeight:700,fontSize:15}}>{s.name}</div>
                {s.address && <div style={{fontSize:13,color:'var(--text-3)',marginTop:2}}><MapPin size={11}/> {s.address}</div>}
              </div>
              <div style={{display:'flex',gap:8}}>
                <button className="btn btn-secondary btn-sm" onClick={()=>setEdit(s)}><Edit2 size={13}/></button>
                <button className="btn btn-danger btn-sm" onClick={()=>setConfirmRemove(s)}><Trash2 size={13}/></button>
              </div>
            </div>
          </div>
        ))}
        {sites.length===0 && <div className="empty-state card"><p>Aucun site configuré</p></div>}
      </div>
      {(showNew||editSite) && <SiteModal site={editSite} onClose={()=>{setEdit(null);setNew(false);}} onSaved={load}/>}
      {confirmRemove && (
        <ConfirmModal
          title="Supprimer le site"
          message={`Supprimer "${confirmRemove.name}" ?`}
          confirmLabel="Supprimer"
          danger
          loading={removing}
          onConfirm={remove}
          onCancel={() => setConfirmRemove(null)}
        />
      )}
    </div>
  );
}

// ── Droits — logique originale ──────────────────────────────
const PERM_ROLE_COLORS = { admin:'var(--red)', manager:'var(--amber)', staff:'var(--accent-light)', gm:'var(--teal)', freelance:'var(--text-3)' };

function PermIcon({ effective, override, roleDefault }) {
  if (override === true)  return <Check size={14} color="var(--teal)" title="Accordé manuellement"/>;
  if (override === false) return <X size={14} color="var(--red)" title="Retiré manuellement"/>;
  if (roleDefault)        return <Check size={14} color="var(--text-3)" title="Accordé par le rôle"/>;
  return <Minus size={14} color="rgba(255,255,255,0.15)" title="Non accordé"/>;
}

function PermissionsTab() {
  const { user: currentUser, refreshPerms } = useAuth();
  const [users, setUsers]              = useState([]);
  const [selectedUser, setUser]        = useState(null);
  const [permissions, setPerms]        = useState([]);
  const [pendingOverrides, setPending] = useState({});
  const [saving, setSaving]            = useState(false);

  useEffect(() => { authApi.users().then(r => setUsers(r.data.users)).catch(()=>{}); }, []);

  const loadUserPerms = useCallback(async (userId) => {
    try { const r = await permissionsApi.byUser(userId); setPerms(r.data.permissions); setPending({}); }
    catch { toast.error('Erreur chargement'); }
  }, []);

  const selectUser = (user) => { setUser(user); loadUserPerms(user.id); };

  const toggle = (code, current) => {
    setPending(prev => {
      const existing = prev[code];
      let next;
      if (existing === undefined) next = !current.effective ? true : false;
      else if (existing === true)  next = false;
      else if (existing === false) next = null;
      else next = true;
      return { ...prev, [code]: next };
    });
  };

  const save = async () => {
    if (!selectedUser) return;
    setSaving(true);
    try {
      await permissionsApi.setUser(selectedUser.id, { overrides: pendingOverrides });
      toast.success('Permissions sauvegardées');
      loadUserPerms(selectedUser.id);
      // Si on vient de modifier ses propres droits, recharger
      if (currentUser?.id === selectedUser.id) refreshPerms();
    } catch { toast.error('Erreur'); }
    finally { setSaving(false); }
  };

  const byCategory = {};
  for (const p of permissions) {
    if (!byCategory[p.category]) byCategory[p.category] = [];
    byCategory[p.category].push(p);
  }
  const hasPending = Object.keys(pendingOverrides).length > 0;

  return (
    <div style={{ display:'grid', gridTemplateColumns:'240px 1fr', gap:16, alignItems:'start' }}>
      <div>
        <div className="card" style={{ marginBottom:12, padding:12 }}>
          <div style={{ fontWeight:600, fontSize:12, marginBottom:8 }}>Légende</div>
          <div style={{ fontSize:11, color:'var(--text-3)', lineHeight:2 }}>
            <Check size={10} style={{display:'inline',marginRight:4}} color="var(--teal)"/>accordé par rôle<br/>
            <Check size={10} style={{display:'inline',marginRight:4}} color="var(--text-3)"/>accordé manuellement<br/>
            <X size={10} style={{display:'inline',marginRight:4}} color="var(--red)"/>retiré manuellement
          </div>
        </div>
        <div className="card" style={{ padding:0, overflow:'hidden' }}>
          {users.map(u => (
            <div key={u.id} onClick={() => selectUser(u)}
              style={{ display:'flex', alignItems:'center', gap:10, padding:'10px 14px', cursor:'pointer',
                borderBottom:'1px solid var(--border)',
                background: selectedUser?.id===u.id ? 'var(--bg-elevated)' : 'transparent',
                borderLeft: selectedUser?.id===u.id ? '3px solid var(--accent)' : '3px solid transparent' }}>
              <div className="avatar" style={{ width:26, height:26, fontSize:10 }}>
                {u.first_name?.[0]}{u.last_name?.[0]}
              </div>
              <div style={{ flex:1, minWidth:0 }}>
                <div style={{ fontWeight:600, fontSize:13, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                  {u.first_name} {u.last_name}
                </div>
                <span style={{ fontSize:11, color: PERM_ROLE_COLORS[u.role]||'var(--text-3)' }}>{u.role}</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div>
        {!selectedUser ? (
          <div className="empty-state card"><Shield size={28} style={{ opacity:0.3 }}/><p>Sélectionnez un utilisateur</p></div>
        ) : (
          <>
            <div className="card" style={{ marginBottom:12, display:'flex', alignItems:'center', gap:12 }}>
              <div className="avatar" style={{ width:32, height:32, fontSize:12 }}>
                {selectedUser.first_name?.[0]}{selectedUser.last_name?.[0]}
              </div>
              <div style={{ flex:1 }}>
                <div style={{ fontWeight:700 }}>{selectedUser.first_name} {selectedUser.last_name}</div>
                <span style={{ fontSize:12, color: PERM_ROLE_COLORS[selectedUser.role] }}>{selectedUser.role}</span>
              </div>
              {hasPending && (
                <div style={{ display:'flex', gap:8 }}>
                  <button className="btn btn-secondary btn-sm" onClick={() => setPending({})}>Annuler</button>
                  <button className="btn btn-primary btn-sm" onClick={save} disabled={saving}>
                    {saving ? <span className="spinner" style={{width:13,height:13}}/> : 'Sauvegarder'}
                  </button>
                </div>
              )}
            </div>
            {Object.entries(byCategory).map(([category, perms]) => (
              <div key={category} className="card" style={{ marginBottom:12, padding:0, overflow:'hidden' }}>
                <div style={{ padding:'8px 16px', borderBottom:'1px solid var(--border)',
                  fontWeight:600, fontSize:12, color:'var(--text-2)',
                  textTransform:'uppercase', letterSpacing:'0.06em' }}>{category}</div>
                {perms.map(p => {
                  const pending = pendingOverrides[p.code];
                  const displayOverride = pending !== undefined ? pending : p.override;
                  const displayEffective = pending !== undefined ? (pending === null ? p.role_default : pending) : p.effective;
                  return (
                    <div key={p.code} onClick={() => toggle(p.code, p)}
                      style={{ display:'flex', alignItems:'center', gap:12, padding:'10px 16px',
                        borderBottom:'1px solid var(--border)', cursor:'pointer',
                        background: pending !== undefined ? 'rgba(124,92,252,0.05)' : 'transparent' }}
                      onMouseEnter={e => e.currentTarget.style.background='var(--bg-elevated)'}
                      onMouseLeave={e => e.currentTarget.style.background=pending!==undefined?'rgba(124,92,252,0.05)':'transparent'}>
                      <PermIcon effective={displayEffective} override={displayOverride} roleDefault={p.role_default}/>
                      <div style={{ flex:1 }}>
                        <div style={{ fontSize:13, fontWeight:displayEffective?500:400,
                          color:displayEffective?'var(--text-1)':'var(--text-3)' }}>{p.label}</div>
                        <div style={{ fontSize:10, color:'var(--text-3)', marginTop:1 }}>
                          {p.code}{p.role_default && <span style={{marginLeft:8}}>· Par défaut pour {p.min_role}+</span>}
                        </div>
                      </div>
                      {displayOverride !== null && displayOverride !== undefined && (
                        <span style={{ fontSize:10, padding:'2px 6px', borderRadius:4,
                          background:displayOverride?'rgba(0,210,180,0.15)':'rgba(255,77,109,0.15)',
                          color:displayOverride?'var(--teal)':'var(--red)' }}>
                          {displayOverride ? 'Accordé' : 'Retiré'}
                        </span>
                      )}
                      {pending !== undefined && <span style={{ fontSize:10, color:'var(--accent-light)' }}>●</span>}
                    </div>
                  );
                })}
              </div>
            ))}
          </>
        )}
      </div>
    </div>
  );
}

// ── Page principale ───────────────────────────────────────────
const TEAM_TABS = [
  ['users',  'Utilisateurs', Users],
  ['sites',  'Sites',        MapPin],
  ['droits', 'Droits',       Shield],
];

export default function AdminTeam() {
  const { can } = useAuth();
  if (!can('admin.users')) return <div className="page-body"><div className="empty-state card"><p>Accès non autorisé</p></div></div>;
  const [tab, setTab] = useState('users');

  return (
    <>
      <div className="page-header">
        <div><h2>Équipe & Accès</h2><p>Utilisateurs, sites et droits</p></div>
      </div>
      <div className="page-body">
        <div className="tabs" style={{marginBottom:20}}>
          {TEAM_TABS.map(([k,l,Icon])=>(
            <div key={k} className={`tab${tab===k?' active':''}`} onClick={()=>setTab(k)}
              style={{display:'flex',alignItems:'center',gap:6}}>
              <Icon size={14}/> {l}
            </div>
          ))}
        </div>
        {tab==='users'  && <UsersTab can={can}/>}
        {tab==='sites'  && <SitesTab can={can}/>}
        {tab==='droits' && <PermissionsTab can={can}/>}
      </div>
    </>
  );
}
